import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useBranding, useBrandingHostname } from '@/hooks/use-branding'
import useLocale, { Language } from '@/hooks/use-locale'

import { Icons } from '@/components/icons'
import { ForumNavUnreadIndicator } from '@/components/layouts/nav/forum-nav-unread-indicator'
import { headerSpacingClassNames, headerWidthClassNames } from '@/components/layouts/nav/header-layout-styles'
import HeaderRightActions from '@/components/layouts/nav/header-right-actions'
import NextImage from '@/components/NextImage'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
  NavigationMenuViewport,
} from '@/components/ui/navigation-menu'

import { filterMainNavByDomain,FORUM_NAV_HREF } from '@/configs/router'

import { HeaderConfig } from '@/types/header-footer-config'
import { MainNavItem } from '@/types/nav.type'

const DEFAULT_WIDTH_NAV = 460

interface MainNavProps {
  items: MainNavItem[]
}

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'> & {
    icon?: keyof typeof Icons
    isNested?: boolean
  }
>(({ className, title, children, href, icon, isNested, ...props }, ref) => {
  const Icon = icon ? Icons[icon] : null
  return (
    <li className={cn('w-full', isNested ? 'pl-4' : '')}>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          href={String(href)}
          className={cn(
            'flex w-full select-none items-center rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            className
          )}
          {...props}
        >
          <div className="flex w-full items-center gap-3">
            {Icon && <Icon className="h-5 w-5 text-primary flex-shrink-0" />}
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium leading-normal">{title}</div>
              {children && <p className="text-sm text-muted-foreground line-clamp-1">{children}</p>}
            </div>
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  )
})
ListItem.displayName = 'ListItem'

const SubMenu = ({ items, level = 0 }: { items: MainNavItem[]; level?: number }) => {
  const { lang } = useLocale()
  const layoutSubMenu: Record<Language, string> = {
    vi: 'grid-cols-3',
    en: 'grid-cols-1',
  }
  return (
    <ul className={cn('grid w-full gap-1.5', level === 1 ? 'grid-cols-1' : layoutSubMenu[lang])}>
      {items.map((item, itemIndex) => {
        const Icon = item.icon ? Icons[item.icon] : null

        return (
          <React.Fragment key={`${level}-${item.title}-${item.href || 'no-href'}-${itemIndex}`}>
            {item.items?.length ? (
              <li className="col-span-full pt-4 pl-2">
                <div className="mb-2 mt-4 text-sm font-medium first:mt-0 flex gap-2">
                  {Icon && <Icon className="h-5 w-5 text-primary flex-shrink-0" />}
                  <span className="text-accent-foreground">{item.title}</span>
                </div>
                <SubMenu items={item.items} level={level + 1} />
              </li>
            ) : (
              <ListItem
                title={item.title}
                href={item.href}
                icon={item.icon}
                isNested={level > 0}
                className="text-muted-foreground"
              >
                {item.description}
              </ListItem>
            )}
          </React.Fragment>
        )
      })}
    </ul>
  )
}

function MainNav({ items }: MainNavProps) {
  const [className, setClassName] = React.useState('')

  return (
    <NavigationMenu>
      <NavigationMenuList>
        {items.map(item => (
          <React.Fragment key={item.title}>
            <NavigationMenuItem className={cn(item.href === FORUM_NAV_HREF && 'relative')}>
              {item.items?.length ? (
                <div className="w-full">
                  <NavigationMenuTrigger
                    className="h-auto bg-transparent text-base"
                    onMouseEnter={() => setClassName(item.className || '')}
                    onFocus={() => setClassName(item.className || '')}
                  >
                    {item.title}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="p-4" style={{ width: `${item.width || DEFAULT_WIDTH_NAV}px` }}>
                      <SubMenu items={item.items} level={0} />
                    </div>
                    {item.thumbnail && (
                      <NextImage
                        src={item.thumbnail}
                        alt={item.title}
                        width={item.width || DEFAULT_WIDTH_NAV}
                        height={340}
                        quality={100}
                        className="w-full h-full object-contain p-6"
                      />
                    )}
                  </NavigationMenuContent>
                </div>
              ) : (
                <Link
                  href={item.href || ''}
                  className={cn(navigationMenuTriggerStyle(), 'h-auto bg-transparent text-base')}
                >
                  {item.title}
                </Link>
              )}
              {item.href === FORUM_NAV_HREF && (
                <ForumNavUnreadIndicator className="absolute -top-1 -right-1" />
              )}
            </NavigationMenuItem>
          </React.Fragment>
        ))}
      </NavigationMenuList>
      <NavigationMenuViewport className={cn('transition-all duration-300 ease-in-out', className)} />
    </NavigationMenu>
  )
}

interface MainNavContentProps {
  items: MainNavItem[]
  children?: React.ReactNode
  headerConfig?: Required<HeaderConfig>
}

export default function MainNavContent({ items, children, headerConfig }: MainNavContentProps) {
  const { brandHref, isFsdsDomain, logoElement } = useBranding()
  const hostname = useBrandingHostname()
  const filteredItems = React.useMemo(
    () => filterMainNavByDomain(items, isFsdsDomain, hostname),
    [items, isFsdsDomain, hostname]
  )
  const layoutPreset = headerConfig?.layoutPreset ?? 'logo-left'
  const isLogoLeft = layoutPreset === 'logo-left' || layoutPreset === 'general-v1-overlay'
  const showLeftSection = headerConfig?.showLeftSection ?? true
  const showRightSection = headerConfig?.showRightSection ?? true
  const rightActions = headerConfig?.rightActions ?? []
  const leftWidthClassName = headerWidthClassNames[headerConfig?.leftSectionWidth ?? 'auto']
  const spacingClassName = headerSpacingClassNames[headerConfig?.spacing ?? 'normal']

  // Filter out items with visible: false for template chrome
  const visibleItems = React.useMemo(
    () => filteredItems.filter(item => item.visible !== false),
    [filteredItems]
  )
  const mainNav = <MainNav items={visibleItems} />

  return (
    <div className={cn('hidden lg:flex lg:w-full lg:items-center', spacingClassName)}>
      {showLeftSection && (
        <div
          className={cn(
            'hidden md:flex items-center',
            isLogoLeft ? 'gap-6 lg:gap-8' : 'space-x-2',
            leftWidthClassName,
            layoutPreset === 'centered' && 'flex-1 justify-center',
            layoutPreset === 'split' && 'flex-1'
          )}
        >
          <Link href={brandHref} className="hidden shrink-0 items-center space-x-2 md:flex" aria-label="Go to Home page">
            {logoElement}
          </Link>
          {isLogoLeft && mainNav}
        </div>
      )}
      {!isLogoLeft && (
        <div className={cn('flex flex-1 items-center justify-center')}>{mainNav}</div>
      )}
      {(showRightSection || children) && (
        <div
          className={cn('ml-auto hidden items-center gap-3 lg:flex', layoutPreset === 'split' && 'flex-1 justify-end')}
        >
          {showRightSection && <HeaderRightActions actions={rightActions} />}
          {children}
        </div>
      )}
    </div>
  )
}
